import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  HeartIcon,
  LightBulbIcon,
  BuildingLibraryIcon,
  UserGroupIcon,
  BanknotesIcon,
  DevicePhoneMobileIcon,
  AcademicCapIcon,
  ArrowRightIcon,
  MapPinIcon,
  ScaleIcon,
} from '@heroicons/react/24/solid';
import Footer from '../components/common/Footer';
import Navigation from '../components/common/Navigation';

const coreValues = [
  {
    icon: ScaleIcon,
    title: 'Trust & integrity',
    description: 'We hold ourselves to the highest standards of security and ethics. Your financial well-being is at the center of every decision we make.',
  },
  {
    icon: HeartIcon,
    title: 'Community focus',
    description: 'Our roots are in Hawaii. We invest in local growth, support small businesses, and help our neighbors build a stronger community.',
  },
  {
    icon: LightBulbIcon,
    title: 'Forward thinking',
    description: 'We embrace innovation while keeping our personal touch. Our services evolve so you get modern banking without losing the human connection.',
  },
];

const differenceItems = [
  { icon: UserGroupIcon, title: 'Member-owned', text: 'You’re not just a customer — you’re an owner. We exist to serve our members, not shareholders.' },
  { icon: BanknotesIcon, title: 'Better rates', text: 'Competitive rates on loans and higher returns on savings, with fewer fees.' },
  { icon: DevicePhoneMobileIcon, title: 'Digital first', text: 'Full-featured online and mobile banking with strong security and real-time access.' },
  { icon: AcademicCapIcon, title: 'Financial education', text: 'Free resources and guidance to help you make confident financial decisions.' },
];

const timeline = [
  { year: '1973', title: 'A vision of community banking', description: 'Willowvale Credit Union was founded by local educators who believed in cooperative banking — fair, accessible financial services for families in Hawaii.' },
  { year: '1985', title: 'Opening our doors wider', description: 'We expanded membership to the broader community and added home loans and small business programs, always with a focus on local needs.' },
  { year: '1995', title: 'Early digital adoption', description: 'We were among the first to offer online banking, setting the stage for the convenient, secure digital experience we offer today.' },
  { year: '2008', title: 'Standing by our members', description: 'During the financial crisis, we stood with our members. Our mortgage assistance programs helped families keep their homes when it mattered most.' },
  { year: '2015', title: 'Banking in your pocket', description: 'Our mobile app put full banking in members’ hands — deposits, transfers, and alerts — without sacrificing security or service.' },
  { year: 'Today', title: 'Here for Hawaii', description: 'We continue to grow with our members: competitive products, local decision-making, and a team that knows your name.' },
];

const communityImpact = [
  { value: '$2M+', label: 'Education grants', description: 'Supporting local schools and scholarships so students can reach their goals.' },
  { value: '10,000+', label: 'Volunteer hours', description: 'Our team gives back through community service and local improvement projects.' },
  { value: '$500K', label: 'Local business support', description: 'Grants, loans, and mentorship to help small businesses in our community thrive.' },
];

const testimonials = [
  {
    quote: 'When I lost my job during the pandemic, Willowvale worked with me to defer my mortgage payments. They showed me that banking can be human.',
    author: 'Michael Rodriguez',
    role: 'Member since 2015',
  },
  {
    quote: "Willowvale helped me start my dream café. They're not just my bank — they're partners in my success.",
    author: 'Sarah Chen',
    role: 'Local business owner',
  },
];

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero */}
      <section className="relative pt-24 pb-20 lg:pt-28 lg:pb-24 bg-[#0a1628] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-primary-400 text-sm font-semibold tracking-widest uppercase mb-4">About us</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Where financial strength
            <br />
            <span className="text-primary-300">meets community values</span>
          </h1>
          <p className="text-slate-300 text-lg sm:text-xl max-w-2xl mx-auto mb-12">
            For decades, Willowvale Credit Union has been more than a place to bank. We’re a member-owned partner in Hawaii — here to help you save, borrow, and grow with trust and local focus.
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-4xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="text-2xl sm:text-3xl font-bold text-white">100K+</div>
              <div className="text-slate-400 text-sm mt-0.5">Members</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="text-2xl sm:text-3xl font-bold text-white">$2.5B+</div>
              <div className="text-slate-400 text-sm mt-0.5">In assets</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="text-2xl sm:text-3xl font-bold text-white">98%</div>
              <div className="text-slate-400 text-sm mt-0.5">Satisfaction</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 col-span-2 lg:col-span-1">
              <div className="text-2xl sm:text-3xl font-bold text-white">Honolulu</div>
              <div className="text-slate-400 text-sm mt-0.5">600 Kapiolani Blvd</div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-6">Our mission</h2>
          <p className="text-slate-600 text-lg leading-relaxed">
            To empower our members’ financial success through personalized service, sound products, and a lasting commitment to our community. When our members thrive, Hawaii thrives.
          </p>
        </div>
      </section>

      {/* Core values */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Our core values</h2>
            <p className="text-slate-600 text-lg">The principles that guide every decision and every member interaction.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {coreValues.map((item) => (
              <div
                key={item.title}
                className="bg-white rounded-2xl p-8 border border-slate-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="p-3 bg-primary-100 rounded-xl w-fit mb-6">
                  <item.icon className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our journey / Timeline */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Our journey</h2>
            <p className="text-slate-600 text-lg">Decades of growth, innovation, and commitment to our community.</p>
          </div>

          <div className="relative">
            <div className="absolute left-4 sm:left-6 top-0 bottom-0 w-px bg-slate-200" aria-hidden />
            <ul className="space-y-0">
              {timeline.map((item, index) => (
                <li key={item.year} className="relative flex gap-8 sm:gap-10 pb-12 last:pb-0">
                  <div className="relative z-10 flex h-8 w-8 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600 font-bold text-sm border-2 border-white shadow-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <span className="text-sm font-semibold text-primary-600">{item.year}</span>
                    <h3 className="text-lg font-bold text-slate-900 mt-1">{item.title}</h3>
                    <p className="text-slate-600 mt-2">{item.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* The Willowvale difference */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">The Willowvale difference</h2>
            <p className="text-slate-600 text-lg">Banking that goes beyond transactions to build lasting relationships.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {differenceItems.map((item) => (
              <div
                key={item.title}
                className="bg-white rounded-xl p-6 border border-slate-100 hover:border-primary-200 transition-colors"
              >
                <div className="p-2.5 bg-primary-100 rounded-lg w-fit mb-4">
                  <item.icon className="h-5 w-5 text-primary-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community impact */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Making a difference</h2>
            <p className="text-slate-600 text-lg">Our commitment to community extends beyond banking.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {communityImpact.map((item) => (
              <div
                key={item.label}
                className="bg-slate-50 rounded-2xl p-8 border border-slate-100 text-center md:text-left"
              >
                <div className="text-3xl font-bold text-primary-600 mb-2">{item.value}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.label}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">In their words</h2>
            <p className="text-slate-600 text-lg">What members say about banking with Willowvale.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((t) => (
              <div key={t.author} className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
                <p className="text-slate-700 italic mb-6">"{t.quote}"</p>
                <div>
                  <div className="font-semibold text-slate-900">{t.author}</div>
                  <div className="text-slate-500 text-sm">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#0a1628]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">Join our member family</h2>
          <p className="text-slate-300 text-lg mb-10">
            Experience member-focused banking. Join Willowvale Credit Union and become part of a community dedicated to your financial success.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors"
            >
              Become a member
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-lg border border-white/20 transition-colors"
            >
              Contact us
            </Link>
          </div>
          <p className="mt-8 text-slate-400 text-sm inline-flex items-center gap-2 justify-center">
            <MapPinIcon className="h-4 w-4 shrink-0" />
            600 Kapiolani Blvd, Honolulu, HI 96813
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
